import logger from './logger';
import { stripReplyFallback } from './replyUtils';

/**
 * Strip <mx-reply> tags from formatted HTML body
 * Matrix reply fallbacks in HTML are wrapped in <mx-reply>...</mx-reply> tags
 *
 * @param {string} html - The formatted HTML body
 * @returns {string} HTML with mx-reply tags removed
 */
function stripMxReplyTag(html) {
  if (!html || typeof html !== 'string') return html;
  return html.replace(/<mx-reply>[\s\S]*?<\/mx-reply>/gi, '');
}

/**
 * Check if a Matrix event mentions a specific user
 * @param {MatrixEvent} event - The Matrix event
 * @param {string} userId - The user ID to check for mentions
 * @param {Object} [options] - Additional options
 * @param {Object} [options.contentOverride] - Content object to use instead of event.getClearContent()/getContent() (e.g. m.new_content for edit events)
 * @param {Object} [options.room] - Matrix room object for display name lookup
 * @param {string} [options.displayName] - Explicit display name to check
 * @returns {boolean} True if the user is mentioned
 */
export function eventMentionsUser(event, userId, options = {}) {
  if (!event || !userId) return false;

  try {
    const clearContent = options.contentOverride || event.getClearContent?.() || event.getContent();
    if (!clearContent) return false;

    // 1. Check m.mentions (MSC3952 - intentional mentions)
    const mentions = clearContent['m.mentions'];
    if (mentions?.user_ids?.includes(userId)) {
      logger.debug('[mentionDetection] User mentioned via m.mentions.user_ids');
      return true;
    }

    // 2. Strip reply fallback from body before checking
    const rawBody = clearContent.body || '';
    const body = stripReplyFallback(rawBody) || '';

    if (body.includes(userId)) {
      logger.debug('[mentionDetection] User mentioned in message body');
      return true;
    }

    // 3. Check formatted_body for Matrix mention pill (strip mx-reply first)
    const rawFormattedBody = clearContent.formatted_body || '';
    const formattedBody = stripMxReplyTag(rawFormattedBody);

    if (formattedBody.includes(`href="https://matrix.to/#/${userId}"`)) {
      logger.debug('[mentionDetection] User mentioned via Matrix pill in formatted_body');
      return true;
    }

    // 4. Check for @display_name mention (requires @ prefix, matching @Name or @[Name] patterns)
    const displayName = options.displayName || getDisplayNameFromRoom(options.room, userId);
    if (displayName && body.includes('@')) {
      const escapedName = escapeRegex(displayName);
      // Match @DisplayName or @[DisplayName] with word boundaries before @
      const regex = new RegExp(
        `(?:^|[\\s.,!?;:'"\\[\\](){}])@(?:\\[${escapedName}\\]|${escapedName})(?:[\\s.,!?;:'"\\[\\](){}]|$)`,
        'i',
      );
      if (regex.test(body)) {
        logger.debug('[mentionDetection] User mentioned by @display name');
        return true;
      }
    }

    return false;
  } catch (error) {
    logger.error('[mentionDetection] Error checking mentions:', error);
    return false;
  }
}

/**
 * Get user's display name from a room
 * @param {Object} room - Matrix room object
 * @param {string} userId - User ID
 * @returns {string|null} Display name or null
 */
function getDisplayNameFromRoom(room, userId) {
  if (!room || !userId) return null;
  const member = room.getMember?.(userId);
  return member?.name || member?.rawDisplayName || null;
}

/**
 * Escape regex special characters
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
